// Shared logic for the Office Dashboard — ported from valuation-dashboard.html.
// Uses backend snake_case report fields. Note: `fees` = original fee, net = fees - discount.

export const WORK_TYPES = ['Valuation Report', 'Stage Bill', 'Stability Certificate', 'Estimation & Valuation'];
export const OFFICES = ['Karaikudi', 'Sivagangai'];
export const CONTACT_TYPES = ['Direct Client', 'Bank Staff', 'DSA', 'Referral'];
export const PAY_MODES = ['Pending', 'Cash', 'UPI', 'Bank Credit'];
export const ADVANCE_MODES = ['Cash', 'UPI', 'Bank Credit'];
export const ADMINS = [
  { user_id: 'admin-sai', name: 'Sai Kishore' },
  { user_id: 'admin-anbazhagan', name: 'Anbazhagan' },
];
export const DAILY_EXP_CATEGORIES = ['Petrol', 'Tea & Snacks', 'Auto', 'Bus', 'Other'];
export const OFFICE_EXP_CATEGORIES = ['Rent', 'Electricity', 'Internet', 'Stationery', 'Tax', 'Other'];
export const ON_HOLD_REASONS = [
  'Document Pending', 'Site Access Issue', 'Bank Clarification Pending',
  'Approved Plan Pending', 'Payment Pending', 'Other',
];

// ---- Contacts (CRM) ----
export const CONTACT_CATEGORIES = [
  { id: 'bank_staff', label: 'Bank Staff', color: '#2563eb', bg: '#dbeafe', dark: '#1e3a8a' },
  { id: 'client', label: 'Client', color: '#16a34a', bg: '#dcfce7', dark: '#14532d' },
  { id: 'dsa', label: 'DSA', color: '#d97706', bg: '#fef3c7', dark: '#78350f' },
  { id: 'referral', label: 'Referral', color: '#7c3aed', bg: '#ede9fe', dark: '#4c1d95' },
];
export const CONTACT_CATEGORY_BY_ID = Object.fromEntries(CONTACT_CATEGORIES.map((c) => [c.id, c]));
// Map NewWorkModal's contact_type string -> CRM category id
export const CONTACT_TYPE_TO_CATEGORY = {
  'Direct Client': 'client',
  'Bank Staff': 'bank_staff',
  'DSA': 'dsa',
  'Referral': 'referral',
};
export const BANK_SUGGESTIONS = [
  'State Bank of India', 'Indian Bank', 'Indian Overseas Bank', 'Canara Bank',
  'Tamilnad Mercantile Bank', 'Repco Bank', 'Repco Home Finance', 'Bank of Maharashtra',
  'Bank of Baroda', 'Bank of India', 'Punjab National Bank', 'Union Bank of India',
  'Truhome Finance Ltd (Shriram Housing Finance Ltd)',
];

export const inr = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN');

export const typeClass = (t) =>
  t === 'Stage Bill' ? 'type-stagebill'
  : t === 'Stability Certificate' ? 'type-stability'
  : t === 'Estimation & Valuation' ? 'type-estimation'
  : 'type-valuation';
export const typeHeaderClass = (t) =>
  t === 'Stage Bill' ? 'stagebill-header'
  : t === 'Stability Certificate' ? 'stability-header'
  : t === 'Estimation & Valuation' ? 'estimation-header'
  : 'valuation-header';

export function daysSince(createdAt) {
  if (!createdAt) return 0;
  const c = new Date(createdAt); const n = new Date();
  const cc = new Date(c.getFullYear(), c.getMonth(), c.getDate());
  const nn = new Date(n.getFullYear(), n.getMonth(), n.getDate());
  return Math.max(0, Math.floor((nn - cc) / 86400000));
}
export function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}
export function overdueBadgeClass(d) {
  if (d >= 7) return 'overdue-7'; if (d >= 3) return 'overdue-3'; if (d >= 1) return 'overdue-1'; return 'overdue-0';
}
export function overdueLabel(d) {
  if (d === 0) return 'Today'; if (d === 1) return '1 day pending'; return d + ' days pending';
}

// ---- fee math ----
export const netFee = (r) => Math.max(0, Number(r.fees || 0) - Number(r.discount || 0));
export const balanceDue = (r) => { const nf = netFee(r); return nf > 0 ? Math.max(0, nf - Number(r.advance || 0)) : 0; };

// ---- sub-tab classification (Works tab) ----
export const isOnHold = (r) => !!r.is_on_hold && !!r.hold_reason && r.hold_reason.trim() !== '';
export const isInBankFeeSubTab = (r) => r.pay_mode === 'Bank Credit' && r.report_done === true && r.collected === true && !isOnHold(r);
export const isInCompletedSubTab = (r) => (r.report_done === true || r.collected === true) && !isOnHold(r) && !isInBankFeeSubTab(r);
export const isArchived = (r) => r.archived === true;

// ---- ready to archive (fully paid + collected) ----
export const isPaid = (r) => {
  if (!r.pay_mode || r.pay_mode === 'Pending') return false;
  if (r.pay_mode === 'UPI') return !!r.upi_shot || !!r.pay_confirmed;
  return !!r.pay_confirmed;
};
export const isAdvanceConfirmed = (r) => {
  if (!r.advance || Number(r.advance) <= 0) return true;
  if (r.advance_mode === 'UPI') return !!r.advance_shot || !!r.advance_confirmed;
  return !!r.advance_confirmed;
};
export const isFullyPaid = (r) => {
  const nf = netFee(r);
  if (nf <= 0) return false;
  if (!isAdvanceConfirmed(r)) return false;
  if (Number(r.advance || 0) >= nf) return true;
  return isPaid(r);
};
export const isReadyToArchive = (r) =>
  r.inspection_done && r.collected && isFullyPaid(r) && !r.archived && r.pay_mode !== 'Bank Credit';

// ---- advance/balance status tags (cash routing) ----
export function advanceStatusInfo(r) {
  const mode = r.advance_mode;
  let modeText = mode || '', tagClass = 'sb-tag-grey', tagText = 'Pending';
  if (mode === 'Cash') {
    const paidTo = r.advance_paid_to;
    if (paidTo === 'Admin') {
      modeText = 'Cash \u00b7 Admin';
      if (r.advance_confirmed) { tagClass = 'sb-tag-green'; tagText = 'Received'; }
      else { tagClass = 'sb-tag-amber'; tagText = 'With Admin'; }
    } else {
      modeText = 'Cash';
      if (r.advance_confirmed) { tagClass = 'sb-tag-green'; tagText = 'Received'; }
      else { tagClass = 'sb-tag-amber'; tagText = 'With Staff'; }
    }
  } else if (mode === 'UPI') {
    const paidTo = r.advance_upi_paid_to || r.advance_paid_to;
    if (paidTo === 'Staff') {
      modeText = 'UPI \u00b7 Staff';
      if (r.advance_confirmed) { tagClass = 'sb-tag-green'; tagText = 'Received'; }
      else { tagClass = 'sb-tag-amber'; tagText = 'With Staff'; }
    } else if (paidTo === 'Admin') {
      modeText = 'UPI \u00b7 Admin';
      if (r.advance_shot && r.advance_confirmed) { tagClass = 'sb-tag-green'; tagText = 'Credited'; }
      else if (r.advance_shot) { tagClass = 'sb-tag-red'; tagText = 'Not Verified'; }
      else { tagClass = 'sb-tag-red'; tagText = 'No Proof'; }
    } else {
      modeText = 'UPI';
      if (r.advance_shot) { tagClass = 'sb-tag-green'; tagText = 'Uploaded'; }
      else { tagClass = 'sb-tag-red'; tagText = 'No Proof'; }
    }
  } else if (mode === 'Bank Credit') {
    modeText = 'Bank';
    if (r.advance_confirmed) { tagClass = 'sb-tag-green'; tagText = 'Credited'; }
    else { tagClass = 'sb-tag-amber'; tagText = 'Not Credited'; }
  }
  return { tagClass, tagText, modeText };
}

export function balanceStatusInfo(r) {
  const mode = r.pay_mode;
  if (!mode || mode === 'Pending') return { tagClass: 'sb-tag-grey', tagText: 'Pending', modeText: '' };
  let modeText = mode, tagClass = 'sb-tag-grey', tagText = 'Pending';
  if (mode === 'Bank Credit') {
    modeText = 'Bank';
    if (r.pay_confirmed) { tagClass = 'sb-tag-green'; tagText = 'Credited'; }
    else { tagClass = 'sb-tag-amber'; tagText = 'Not Credited'; }
  } else if (mode === 'Cash') {
    const to = r.pay_cash_paid_to;
    modeText = to === 'Admin' ? 'Cash \u00b7 Admin' : to === 'Staff' ? 'Cash \u00b7 Staff' : 'Cash';
    if (r.pay_confirmed) { tagClass = 'sb-tag-green'; tagText = 'Received'; }
    else if (r.collected) { tagClass = 'sb-tag-amber'; tagText = to === 'Admin' ? 'With Admin' : 'With Staff'; }
    else { tagClass = 'sb-tag-grey'; tagText = 'Pending'; }
  } else if (mode === 'UPI') {
    const to = r.pay_upi_paid_to;
    if (to === 'Staff') {
      modeText = 'UPI \u00b7 Staff';
      if (r.pay_confirmed) { tagClass = 'sb-tag-green'; tagText = 'Received'; }
      else { tagClass = 'sb-tag-amber'; tagText = 'With Staff'; }
    } else if (to === 'Admin') {
      modeText = 'UPI \u00b7 Admin';
      if (r.upi_shot && r.pay_confirmed) { tagClass = 'sb-tag-green'; tagText = 'Credited'; }
      else if (r.upi_shot) { tagClass = 'sb-tag-red'; tagText = 'Not Verified'; }
      else { tagClass = 'sb-tag-grey'; tagText = 'Pending'; }
    } else {
      modeText = 'UPI';
      tagClass = r.upi_shot ? 'sb-tag-green' : 'sb-tag-grey';
      tagText = r.upi_shot ? 'Uploaded' : 'Pending';
    }
  }
  return { tagClass, tagText, modeText };
}

// ---- report status cycle: NotCompleted -> OnHold -> Completed -> Collected -> reset ----
export function cycleReportPatch(r) {
  if (!r.report_done && !r.collected && !r.is_on_hold) {
    return { is_on_hold: true, report_done: false, collected: false };
  }
  if (r.is_on_hold) {
    return { is_on_hold: false, hold_reason: null, hold_notes: null, report_done: true, collected: false, collected_date: null };
  }
  if (r.report_done && !r.collected) {
    return { collected: true, collected_date: new Date().toISOString() };
  }
  return { report_done: false, collected: false, is_on_hold: false, hold_reason: null, hold_notes: null, collected_date: null };
}

// ---- finance date helpers ----
export const todayKey = () => new Date().toISOString().slice(0, 10);
export function formatFinanceDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[dt.getDay()]}, ${d} ${months[m - 1]} ${y}`;
}
export function shiftDate(ymd, delta) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  if (dt > t) return ymd;
  return dt.toISOString().slice(0, 10);
}
export const isoToDateKey = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
export function daysBetween(fromYmd, toYmd) {
  const [y1, m1, d1] = fromYmd.split('-').map(Number);
  const [y2, m2, d2] = toYmd.split('-').map(Number);
  return Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000);
}
